import { ProjectApi } from "@api/project-api/project-api";
import { JSONValue } from "@faro-lotv/foundation";
import { createMutationSetElementMetaData } from "@faro-lotv/service-wires";
import { AutodeskService } from "@services/integrations-service/autodesk/autodesk-service";
import { BaseService } from "@services/integrations-service/base-service";
import {
  IntegrationCompany,
  IntegrationProject,
  ProjectIntegrationsMap,
} from "@services/integrations-service/integrations-service-types";
import { ProcoreService } from "@services/integrations-service/procore/procore-service";
import { isProjectIntegrations } from "@src/services/integrations-service/integrations-type-guards";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface Props {
  /** Project API client instance */
  projectApiClient: ProjectApi;

  /** Callback to re-authorize the integration */
  reauthorize: (integrationId: FaroCoreApiTypes.IntegrationId) => Promise<void>;
}

/** Service that provides methods to manage 3rd party integrations with a project */
export class IntegrationsService {
  #projectApiClient: ProjectApi;
  #services: { [key in FaroCoreApiTypes.IntegrationId]: BaseService };

  constructor({ projectApiClient, reauthorize }: Props) {
    this.#projectApiClient = projectApiClient;
    this.#services = {
      procore: new ProcoreService({
        reauthorize,
      }),
      autodesk: new AutodeskService({
        reauthorize,
      }),
    };
  }

  /**
   * @returns The ProjectIntegrations entity of the current project
   * If the project connection data still has the Legacy (HB) data format update it to the XG format
   * @throws {ProjectApiError} if it fails to fetch data from Project Api
   * @throws {Error} if it fails to update the data format
   */
  public async getProjectIntegrations(): Promise<
    ProjectIntegrationsMap | undefined
  > {
    const root = await this.#projectApiClient.getRootIElement();
    const projectIntegrations = root.metaDataMap?.projectIntegrations;

    if (!projectIntegrations) {
      return;
    }

    if (!isProjectIntegrations(projectIntegrations)) {
      throw Error("Failed to validate the project integrations object");
    }

    return await this.updateDataFormat(projectIntegrations);
  }

  /**
   * @returns the companies of an integration
   * @throws {Error} if it fails to get the companies
   * @param integrationId ID of the integration
   */
  public async getIntegrationCompanies(
    integrationId: FaroCoreApiTypes.IntegrationId
  ): Promise<IntegrationCompany[]> {
    return await this.#services[integrationId].getIntegrationCompanies();
  }

  /**
   * @returns projects for a given company of an integration
   * @throws {Error} if it fails to get the projects
   * @param integrationCompany Integration company entity
   */
  public async getIntegrationProjects(
    integrationCompany: IntegrationCompany
  ): Promise<IntegrationProject[]> {
    return await this.#services[
      integrationCompany.integrationId
    ].getIntegrationProjects(integrationCompany.id);
  }

  /**
   * Connects the XG project to an integration project.
   * @returns The updated ProjectIntegrations entity
   */
  public async connectProject({
    integrationId,
    id,
    name,
    companyId,
    issuesOrObservationsBcfProjectId,
    rfisBcfProjectId,
    type,
  }: IntegrationProject): Promise<ProjectIntegrationsMap> {
    const projectIntegrations: ProjectIntegrationsMap =
      (await this.getProjectIntegrations()) ?? {};

    switch (integrationId) {
      case "procore":
        projectIntegrations.procore = {
          projectName: name,
          projectId: id,
          companyId,
          providerId: "procore",
        };
        projectIntegrations["procore.observations"] = {
          bcfProjectId: issuesOrObservationsBcfProjectId,
        };
        projectIntegrations["procore.rfis"] = {
          bcfProjectId: rfisBcfProjectId,
        };
        break;
      case "autodesk":
        projectIntegrations.autodesk = {
          projectName: name,
          projectId: id,
          hubId: companyId,
          providerId: "autodesk",
        };

        projectIntegrations["autodesk-acc.issues"] =
          type === "ACC"
            ? {
                bcfProjectId: issuesOrObservationsBcfProjectId,
              }
            : null;

        projectIntegrations["autodesk-acc.rfis"] =
          type === "ACC"
            ? {
                bcfProjectId: rfisBcfProjectId,
              }
            : null;

        projectIntegrations["autodesk-bim360.issues"] =
          type === "BIM360"
            ? {
                bcfProjectId: issuesOrObservationsBcfProjectId,
              }
            : null;

        projectIntegrations["autodesk-bim360.rfis"] =
          type === "BIM360"
            ? {
                bcfProjectId: rfisBcfProjectId,
              }
            : null;
        break;
    }

    return await this.setProjectIntegrations(projectIntegrations);
  }

  /**
   * Disconnects an integration from a project.
   * To achieve this it sets each project integration value to null for each user level integration
   * @returns The updated ProjectIntegrations entity
   * @param integrationId ID of the integration to disconnect
   * @throws {Error} if the Project Api client is not defined
   * @throws {ProjectApiError} if it fails to update or fetch data from Project Api
   */
  public async disconnectProject(
    integrationId: FaroCoreApiTypes.IntegrationId
  ): Promise<ProjectIntegrationsMap | undefined> {
    const projectIntegrations = await this.getProjectIntegrations();

    if (!projectIntegrations) {
      return;
    }

    switch (integrationId) {
      case "procore":
        projectIntegrations.procore = null;
        projectIntegrations["procore.observations"] = null;
        projectIntegrations["procore.rfis"] = null;
        break;
      case "autodesk":
        projectIntegrations.autodesk = null;
        projectIntegrations["autodesk-acc.issues"] = null;
        projectIntegrations["autodesk-acc.rfis"] = null;
        projectIntegrations["autodesk-bim360.issues"] = null;
        projectIntegrations["autodesk-bim360.rfis"] = null;
        break;
    }

    return await this.setProjectIntegrations(projectIntegrations);
  }

  /**
   * Stores the ProjectIntegrations entity in the metadata of the ProjectAPI root IElement
   * @param projectIntegrations ProjectIntegrations entity
   * @returns the updated ProjectIntegrations entity
   * @throws {Error} if the Project Api client is not defined
   * @throws {ProjectApiError} if it fails to update or fetch data from Project Api
   */
  private async setProjectIntegrations(
    projectIntegrations: ProjectIntegrationsMap
  ): Promise<ProjectIntegrationsMap> {
    const { id } = await this.#projectApiClient.getRootIElement();

    const mutation = createMutationSetElementMetaData(id, [
      {
        key: "ProjectIntegrations",
        value: projectIntegrations as JSONValue,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by Project Api backend
        skipIfPresent: false,
      },
    ]);

    await this.#projectApiClient.applyMutations([mutation]);
    const updatedProjectIntegrations = await this.getProjectIntegrations();

    if (!updatedProjectIntegrations) {
      throw Error(
        "Integrations were updated for the project but they were later not available in the backend."
      );
    }

    return updatedProjectIntegrations;
  }

  /**
   * Checks if any integration still has the Legacy (HB) project connection data format. If that's the
   * case it then updates the data to the Sphere XG format.
   * @returns The project integrations map with the updated data format
   * @param data project integrations map
   * @throws {Error} if it fails to update the data format.
   */
  private async updateDataFormat(
    data: ProjectIntegrationsMap
  ): Promise<ProjectIntegrationsMap> {
    if (!this.isLegacyDataFormat(data)) {
      return data;
    }

    for (const service of Object.values(this.#services)) {
      if (service.isLegacyDataFormat(data)) {
        data = await service.updateDataFormat(data);
      }
    }

    return await this.setProjectIntegrations(data);
  }

  /**
   * @returns Whether the project connection data format is of type Legacy (HB).
   * @param data Project integrations map
   */
  private isLegacyDataFormat(data: ProjectIntegrationsMap): boolean {
    return Object.values(this.#services).some((service) =>
      service.isLegacyDataFormat(data)
    );
  }
}
